import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import type { DateInvite, DateLength, VenueChoice } from '@/api/types';
import { endpoints } from '@/api';
import { qk } from '@/api/queries';
import { isGuest, useAuth } from '@/stores/auth';
import { toast } from '@/stores/toasts';
import { Sheet } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Switch, TextAreaField } from '@/components/ui/Field';
import { Mascot, PRESS } from '@/components/fun';
import { cn } from '@/lib/cn';
import { localTimezone } from '@/lib/time';
import { useInviteComposer } from './composer';
import { dateInviteError } from './errors';
import { buildIcs, canNativeShare, copyText, downloadIcs, nativeShare, rememberLink, whatsappHref, whenLabel } from './share';
import { SlotBuilder } from './SlotBuilder';
import { PlannerSheet } from './PlannerDeck';
import { quickChips, slotInputs, slotLabel } from './slots';
import { DEFAULT_VENUE, NO_THEME, NO_THEME_LABEL, venueOf } from '@/features/venues/catalog';
import { VenuePicker } from '@/features/venues/VenuePicker';
import { useVenuesEnabled } from '@/features/venues/VenueStage';
import { ReadyMessages, useReadyMessage } from './ReadyMessages';
import { DirectTo, useDirectPerson } from './DirectTo';
import { HabitRealtime } from '@/features/links/HabitRealtime';
import { SeriesSheet } from '@/features/series/SeriesSheet';

const LENGTHS: DateLength[] = [30, 60];
const NOTE_MAX = 140;
/** Mirrors the server's "no links" rule so people learn before they submit. */
const LINKISH = /(https?:\/\/|www\.|\b[a-z0-9-]{2,}\.(com|net|org|io|in|co|me|app|ly|gg|xyz|link|info|biz|dev|ai|tv|us|uk|to|be|site|online|club|page|click|lol|fun|live|chat|social)\b)/i;

export { toLocalInput } from './slots';

/** Start with the first quick chip offered, so "Offer times" is one tap from done. */
function defaultTimes(): Date[] {
  const q = quickChips();
  return q.length ? [q[0].at] : [];
}

function Segmented<T extends string | number>({ label, value, options, onChange, testId }: { label: string; value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; testId: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="grid grid-cols-2 gap-1 rounded-full bg-raised p-1">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'h-10 rounded-full px-4 text-[15px] font-semibold tabular-nums transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg',
            value === o.value ? 'bg-primary text-ground' : 'text-muted hover:text-fg',
          )}
          data-testid={`${testId}-${o.value}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Create sheet (SPEC_V6_1 §8, SPEC_V6_2 §5, SPEC_V10): one tap by default —
 * "Send a 30-min date link", now. "Change details" opens length, now or
 * offered times, the venue and a note (kept mounted, so nothing is lost).
 */
export function CreateInviteSheet() {
  const { open, prefill, close, share } = useInviteComposer();
  const qc = useQueryClient();
  /** v9 K1: straight to a connection — no link, they get it in Popin. */
  const to = useDirectPerson(prefill.to);
  const [length, setLength] = useState<DateLength>(30);
  const [when, setWhen] = useState<'now' | 'times'>('now');
  const [times, setTimes] = useState<Date[]>([]);
  const [auto, setAuto] = useState(true);
  const [note, setNote] = useState('');
  const [venue, setVenue] = useState<VenueChoice>(DEFAULT_VENUE);
  const [details, setDetails] = useState(false);
  const venuesOn = useVenuesEnabled();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const offer = !!prefill.when && prefill.when !== 'now';
    setVenue(DEFAULT_VENUE);
    setLength(prefill.length ?? 30);
    setWhen(offer ? 'times' : 'now');
    setTimes(defaultTimes());
    setAuto(true);
    setNote('');
    setErr(null);
    // Offering times needs the picker, so it starts open.
    setDetails(offer);
  }, [open, prefill]);

  const timeErr = when === 'times' && times.length === 0 ? 'Offer at least one time.' : null;
  const noteErr = note.length > NOTE_MAX ? 'Up to 140 characters.' : LINKISH.test(note) ? 'Notes can’t include links.' : null;
  const v = venuesOn ? venueOf(venue) : null;
  const summary = [`${length} min`, when === 'now' ? 'Now' : `${times.length} ${times.length === 1 ? 'time' : 'times'}`, v ? v.short : venuesOn && venue === NO_THEME ? NO_THEME_LABEL : null, note.trim() ? '+ a note' : null].filter((x): x is string => !!x);

  const create = async () => {
    if (busy || timeErr || noteErr) return;
    setBusy(true);
    setErr(null);
    try {
      const inv = await endpoints.createDateInvite({
        ...(when === 'times'
          ? {
              slots: slotInputs(times, length),
              auto_confirm: auto,
              note: note.trim() || undefined,
              timezone: localTimezone(),
              venue: venuesOn ? venue : undefined,
            }
          : {
              length_min: length,
              starts_at: null,
              now: true,
              note: note.trim() || undefined,
              timezone: localTimezone(),
              venue: venuesOn ? venue : undefined,
            }),
        ...(to ? { to_user_id: to.id } : {}),
      });
      rememberLink(inv.id, inv.url);
      qc.setQueryData<{ invites: DateInvite[] }>(qk.dateInvites, (p) => (p ? { invites: [inv, ...p.invites.filter((x) => x.id !== inv.id)] } : p));
      void qc.invalidateQueries({ queryKey: qk.dateInvites });
      if (to) {
        close();
        toast.success(`Invite sent to ${to.display_name ?? 'them'}`, when === 'times' ? 'They’ll pick a time in Popin.' : 'Meet in the lobby when you’re both ready.');
      } else share(inv);
    } catch (e) {
      setErr(dateInviteError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={close}
      title={to ? `Invite ${to.display_name ?? 'them'} to a date` : 'Invite someone'}
      description={to ? 'It goes straight to them in Popin.' : 'Make a link. They tap yes. You meet on video.'}
      testId="invite-create-sheet"
    >
      <div className="space-y-4" data-direct={to ? 'true' : undefined}>
        {to && <DirectTo person={to} />}
        <div className="flex flex-wrap items-center gap-1.5" data-testid="invite-summary">
          {summary.map((x) => (
            <span key={x} className="rounded-full bg-raised px-3 py-1 text-[13px] font-medium tabular-nums">
              {x}
            </span>
          ))}
        </div>
        {err && (
          <p role="alert" className="text-[15px] font-medium text-danger" data-testid="invite-create-error">
            {err}
          </p>
        )}
        <Button size="xl" block onClick={() => create()} loading={busy} disabled={!!timeErr || !!noteErr} icon="send" data-testid="invite-create">
          {to ? `Send a ${length}-min date invite` : `Send a ${length}-min date link`}
        </Button>
        <button
          type="button"
          onClick={() => setDetails((d) => !d)}
          aria-expanded={details}
          aria-controls="invite-details-panel"
          className="mx-auto flex h-11 items-center gap-1 rounded-full px-4 text-[15px] font-medium text-muted transition-colors hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg"
          data-testid="invite-details"
        >
          {details ? 'Hide details' : 'Change details'} <Icon name="chevron-down" size={16} className={cn('transition-transform', details && 'rotate-180')} />
        </button>
        <div id="invite-details-panel" hidden={!details} className="space-y-5 border-t border-line pt-4">
          <div>
            <p className="mb-2 text-[15px] font-semibold">How long?</p>
            <Segmented label="Date length" value={length} onChange={setLength} testId="invite-length" options={LENGTHS.map((l) => ({ value: l, label: `${l} min` }))} />
            <p className="mt-1.5 text-[13px] text-muted">{length === 30 ? 'Warm-up, a game, one deep question.' : 'Stories, two games, big dreams.'}</p>
          </div>
          <div>
            <p className="mb-2 text-[15px] font-semibold">When?</p>
            <Segmented
              label="When"
              value={when}
              onChange={setWhen}
              testId="invite-when"
              options={[
                { value: 'now', label: 'Now' },
                { value: 'times', label: 'Offer times' },
              ]}
            />
            {when === 'times' ? (
              <div className="mt-3 space-y-3">
                <p className="text-[13px] text-muted">Up to 5 times. They tap the ones that work.</p>
                <SlotBuilder value={times} onChange={setTimes} testId="invite-slots" />
                <Switch label="Auto-confirm one pick" description="One time works for them? It’s a date." checked={auto} onChange={setAuto} testId="invite-auto-confirm" />
              </div>
            ) : (
              <p className="mt-1.5 text-[13px] text-muted">Works for 7 days. Meet when you’re both in the lobby.</p>
            )}
          </div>
          {venuesOn && (
            <div>
              <p className="mb-2 text-[15px] font-semibold">Where?</p>
              <VenuePicker value={venue} onPick={setVenue} size="sm" testId="invite-venue" label="Where is the date?" />
              <p className="mt-1.5 text-[13px] text-muted">Sets the scene: a frame and soft sounds. Or keep it plain with No theme.</p>
            </div>
          )}
          <TextAreaField
            label="A note (optional)"
            placeholder="Chai after? Bring your best pun."
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX + 20))}
            rows={2}
            counter={`${note.length}/${NOTE_MAX}`}
            error={noteErr}
            hint="Shown on the invite. No links."
            data-testid="invite-note"
          />
        </div>
        <p className="flex items-start gap-1.5 text-[13px] leading-relaxed text-muted">
          <Icon name="shield" size={14} className="mt-0.5 shrink-0" />
          {to ? 'Only you two see it. They can say no, no reason needed.' : 'First yes is your date. They only see your first name and character.'}
        </p>
      </div>
    </Sheet>
  );
}

/** Share sheet: WhatsApp first with a ready message, then copy, more apps, calendar or lobby. */
export function ShareInviteSheet() {
  const { shared: inv, closeShare, share: reopen } = useInviteComposer();
  const me = useAuth((s) => s.me);
  const [planFor, setPlanFor] = useState<DateInvite | null>(null);
  const [copied, setCopied] = useState<boolean | null>(null);
  useEffect(() => setCopied(null), [inv?.id]);
  const url = inv?.url ?? '';
  const msg = useReadyMessage(url, inv?.length_min ?? 30);
  const native = canNativeShare();

  const copy = async () => setCopied(await copyText(url));

  const slots = inv?.slots ?? [];
  return (
    <>
      <Sheet open={!!inv} onClose={closeShare} title="Now send it" description="It works without the app." testId="invite-share-sheet">
        {inv && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Mascot avatar={me?.avatar} size={48} mood="cheer" />
              <div className="min-w-0">
                <p className="text-[17px] font-semibold leading-tight tabular-nums">
                  {inv.length_min}-min date · {slots.length ? `${slots.length} ${slots.length === 1 ? 'time' : 'times'} offered` : whenLabel(inv.starts_at).toLowerCase()}
                </p>
                {slots.length > 0 && (
                  <p className="truncate text-[13px] tabular-nums text-muted" data-testid="invite-share-slots">
                    {slots.map((s) => `${slotLabel(s.starts_at).day} ${slotLabel(s.starts_at).time}`).join(' · ')}
                  </p>
                )}
                {inv.note && <p className="truncate text-[15px] text-muted">“{inv.note}”</p>}
              </div>
            </div>

            {url ? (
              <>
                <ReadyMessages msg={msg} />
                <motion.a
                  {...PRESS}
                  href={whatsappHref(msg.text)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-green px-6 text-[17px] font-semibold text-night focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg"
                  data-testid="invite-share-whatsapp"
                >
                  <Icon name="chat" size={20} /> Send on WhatsApp
                </motion.a>
                <div>
                  <div className="flex items-stretch gap-2">
                    <input
                      readOnly
                      value={url}
                      aria-label="Invite link"
                      onFocus={(e) => e.currentTarget.select()}
                      className="h-12 min-w-0 flex-1 rounded-[16px] border border-line bg-ground px-4 text-[15px] text-fg"
                      data-testid="invite-link"
                    />
                    <Button variant="subtle" icon={copied ? 'check' : 'copy'} onClick={() => void copy()} className="h-12" data-testid="invite-copy">
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                  <p className="mt-2 text-[15px]" role="status" aria-live="polite">
                    {copied === true && (
                      <span className="font-semibold text-go" data-testid="invite-copied">
                        Copied! Paste it in your chat.
                      </span>
                    )}
                    {copied === false && <span className="text-fg">Couldn’t copy. Press and hold the link.</span>}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {native && (
                    <Button variant="subtle" icon="send" onClick={() => void nativeShare('A date on Popin', msg.line, url)} data-testid="invite-share-native">
                      More apps…
                    </Button>
                  )}
                  {inv.starts_at ? (
                    <Button variant="subtle" icon="calendar" onClick={() => downloadIcs(buildIcs(inv, null, url))} data-testid="invite-ics" className={cn(!native && 'col-span-2')}>
                      Add to calendar
                    </Button>
                  ) : (
                    <Link
                      to={`/dates/${inv.id}`}
                      onClick={closeShare}
                      className={cn('inline-flex h-11 items-center justify-center gap-2 rounded-full bg-raised px-5 text-[15px] font-semibold transition-colors hover:bg-line', !native && 'col-span-2')}
                      data-testid="invite-open-lobby"
                    >
                      <Icon name="cam" size={18} /> Wait in lobby
                    </Link>
                  )}
                </div>
              </>
            ) : (
              <p className="text-[15px] text-muted">This link was made on another device. Make a new one from Friends → Dates.</p>
            )}
            {inv.status === 'open' && !inv.planner?.mine_done && (
              <button
                type="button"
                onClick={() => {
                  setPlanFor(inv);
                  closeShare();
                }}
                className="flex w-full items-center gap-3 rounded-card bg-raised px-4 py-3.5 text-left transition-colors hover:bg-line focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg"
                data-testid="invite-host-planner"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-semibold">Plan your side (optional)</span>
                  <span className="block text-[13px] text-muted">Vibe, a prop, this-or-that. 30 seconds.</span>
                </span>
                <Icon name="chevron-right" size={18} />
              </button>
            )}
            <p className="text-[13px] leading-relaxed text-muted">
              First yes is your date. Cancel anytime in <span className="font-semibold text-fg">Friends → Dates</span>.
            </p>
          </div>
        )}
      </Sheet>
      <PlannerSheet
        invite={planFor}
        onClose={() => {
          const p = planFor;
          setPlanFor(null);
          if (p) reopen(p);
        }}
      />
    </>
  );
}

/** Mounted once (App): the create + share sheets any surface can open. */
export function InviteComposerHost() {
  const status = useAuth((s) => s.status);
  const guest = useAuth((s) => isGuest(s.me));
  if (status !== 'authed' || guest) return null;
  return (
    <>
      <CreateInviteSheet />
      <ShareInviteSheet />
      <SeriesSheet />
      <HabitRealtime />
    </>
  );
}
